import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Plus, Upload } from 'lucide-react';
import MediaPreview from '../MediaPreview';
import MediaThumbnailGallery from '../MediaThumbnailGallery';
import { Button } from "../ui/button";
import { toast } from "sonner";
import { VideoCaption } from '../VideoCaptionsManager';
import { MediaItem, createMediaItem, createDefaultVideoConfig, generateVideoThumbnail } from '../../types/media';

interface MultiMediaPreviewProps {
  mediaItems: MediaItem[];
  onMediaItemsChange: (items: MediaItem[]) => void;
  selectedPlatforms?: string[];
  selectedPlatformNames?: string[]; // Platform names like 'instagram', 'x', etc
  onError: (error: string) => void;
  onWarning?: (warning: string) => void;
  needsMobileFormat?: boolean;
  onOpenCropper?: () => void;
  uploading?: boolean;
  generatingCaptions?: boolean;
  onRecommendVideo?: () => void;
  recommendingVideo?: boolean;
  onCategorySelect?: (category: string) => void;
  selectedCategory?: string | null;
  unifyFormat?: boolean;
  onUnifyFormatChange?: (unify: boolean) => void;
  showUnifyOption?: boolean;
  maxDuration?: number;
}

export default function MultiMediaPreview({
  mediaItems,
  onMediaItemsChange,
  selectedPlatforms = [],
  selectedPlatformNames = [],
  onError,
  onWarning,
  needsMobileFormat = false,
  onOpenCropper,
  uploading = false,
  generatingCaptions = false,
  onRecommendVideo,
  recommendingVideo = false,
  onCategorySelect,
  selectedCategory,
  unifyFormat = false,
  onUnifyFormatChange,
  showUnifyOption = false,
  maxDuration = 140,
}: MultiMediaPreviewProps): JSX.Element {
  const [selectedItemId, setSelectedItemId] = useState<string>(
    mediaItems.length > 0 ? mediaItems[0].id : ''
  );

  // Keep selectedItemId in sync when items change
  useEffect(() => {
    if (mediaItems.length > 0) {
      const selectedExists = mediaItems.some(item => item.id === selectedItemId);
      if (!selectedExists) {
        setSelectedItemId(mediaItems[0].id);
      }
    }
  }, [mediaItems, selectedItemId]);

  // Get selected media item
  const selectedItem = mediaItems.find(item => item.id === selectedItemId);

  // Handle adding new media via file picker
  const handleAddMedia = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,video/*';
    input.multiple = true;
    input.onchange = (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      handleMultipleFiles(files);
    };
    input.click();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaItems, onMediaItemsChange]);

  // Reference to latest mediaItems so async thumbnail generation can read fresh state
  const mediaItemsRef = useRef(mediaItems);
  mediaItemsRef.current = mediaItems;

  // Handle multiple file additions - always allow, warnings shown in gallery
  const handleMultipleFiles = useCallback((files: File[]) => {
    const newItems: MediaItem[] = [];

    files.forEach((file) => {
      const ext = file.name.toLowerCase().split('.').pop() || '';
      const videoExts = ['mp4', 'mov', 'avi', 'webm', 'mkv'];
      const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
      const isVideo = file.type.startsWith('video/') || videoExts.includes(ext);
      const isImage = file.type.startsWith('image/') || imageExts.includes(ext);

      if (!isVideo && !isImage) {
        toast.error(`Unsupported file type: ${file.name}`);
        return;
      }

      const preview = URL.createObjectURL(file);
      const item = createMediaItem(
        isVideo ? 'video' : 'image',
        file,
        isVideo ? 'local-video' : '',
        preview
      );
      item.order = mediaItems.length + newItems.length;

      newItems.push(item);
    });

    if (newItems.length > 0) {
      const updated = [...mediaItems, ...newItems];
      onMediaItemsChange(updated);
      // Select the first new item
      if (mediaItems.length === 0) {
        setSelectedItemId(newItems[0].id);
      }
      if (newItems.length > 1) {
        toast.success(`Added ${newItems.length} media items`);
      }

      // Generate thumbnails for video items asynchronously
      newItems.forEach((item) => {
        if (item.type === 'video' && item.preview) {
          generateVideoThumbnail(item.preview).then((thumbnailUrl) => {
            // Read the latest state via ref and patch in the thumbnail
            const current = mediaItemsRef.current;
            const patched = current.map(m =>
              m.id === item.id ? { ...m, thumbnailUrl } : m
            );
            onMediaItemsChange(patched);
          }).catch(() => {
            // Silently fail - the gallery will show a fallback icon
          });
        }
      });
    }
  }, [mediaItems, onMediaItemsChange]);

  // Always handle paste at this level so we ADD pasted media as new items instead of replacing.
  // When mediaItems.length === 0, MediaPreview is not mounted so only this listener runs.
  // When mediaItems.length > 0, we use capture + stopImmediatePropagation so our handler runs first
  // and MediaPreview's paste (which would replace the selected item) never runs.
  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === 'file') {
          if (item.type.indexOf('image') !== -1 || item.type.indexOf('video') !== -1) {
            const file = item.getAsFile();
            if (file) {
              files.push(file);
            }
          }
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        e.stopImmediatePropagation();
        handleMultipleFiles(files);
        toast.success(files.length === 1 ? 'Image pasted from clipboard!' : `${files.length} items pasted from clipboard!`);
      }
    };

    window.addEventListener('paste', handlePaste as EventListener, true);
    return () => window.removeEventListener('paste', handlePaste as EventListener, true);
  }, [handleMultipleFiles]);

  // Handle reordering from the thumbnail gallery
  const handleReorder = useCallback((reorderedItems: MediaItem[]) => {
    onMediaItemsChange(reorderedItems);
  }, [onMediaItemsChange]);

  // Handle deleting a media item
  const handleDeleteItem = useCallback((id: string) => {
    const updatedItems = mediaItems
      .filter(item => item.id !== id)
      .map((item, index) => ({ ...item, order: index }));

    onMediaItemsChange(updatedItems);

    // Select another item if the deleted one was selected
    if (id === selectedItemId && updatedItems.length > 0) {
      setSelectedItemId(updatedItems[0].id);
    }
  }, [mediaItems, selectedItemId, onMediaItemsChange]);

  // --- Handlers for the currently selected item's MediaPreview ---

  const handleMediaPathChange = useCallback((path: string) => {
    if (!selectedItem) return;
    const updatedItems = mediaItems.map(item =>
      item.id === selectedItemId ? { ...item, path } : item
    );
    onMediaItemsChange(updatedItems);
  }, [mediaItems, selectedItemId, selectedItem, onMediaItemsChange]);

  const handleMediaPreviewChange = useCallback((preview: string) => {
    if (!selectedItem) return;
    const updatedItems = mediaItems.map(item =>
      item.id === selectedItemId ? { ...item, preview } : item
    );
    onMediaItemsChange(updatedItems);
  }, [mediaItems, selectedItemId, selectedItem, onMediaItemsChange]);

  const handleFileChange = useCallback((file: File | null) => {
    if (!selectedItem) return;
    const updatedItems = mediaItems.map(item =>
      item.id === selectedItemId ? { ...item, localFile: file } : item
    );
    onMediaItemsChange(updatedItems);
  }, [mediaItems, selectedItemId, selectedItem, onMediaItemsChange]);

  const handleVideoDurationChange = useCallback((duration: number) => {
    if (!selectedItem || selectedItem.type !== 'video') return;
    const updatedItems = mediaItems.map(item =>
      item.id === selectedItemId
        ? { ...item, duration, videoConfig: item.videoConfig || createDefaultVideoConfig(duration) }
        : item
    );
    onMediaItemsChange(updatedItems);
  }, [mediaItems, selectedItemId, selectedItem, onMediaItemsChange]);

  const handleVideoStartTimeChange = useCallback((startTime: number) => {
    if (!selectedItem || selectedItem.type !== 'video' || !selectedItem.videoConfig) return;
    const updatedItems = mediaItems.map(item =>
      item.id === selectedItemId && item.videoConfig
        ? { ...item, videoConfig: { ...item.videoConfig, startTime } }
        : item
    );
    onMediaItemsChange(updatedItems);
  }, [mediaItems, selectedItemId, selectedItem, onMediaItemsChange]);

  const handleVideoEndTimeChange = useCallback((endTime: number) => {
    if (!selectedItem || selectedItem.type !== 'video' || !selectedItem.videoConfig) return;
    const updatedItems = mediaItems.map(item =>
      item.id === selectedItemId && item.videoConfig
        ? { ...item, videoConfig: { ...item.videoConfig, endTime } }
        : item
    );
    onMediaItemsChange(updatedItems);
  }, [mediaItems, selectedItemId, selectedItem, onMediaItemsChange]);

  const handleCaptionsChange = useCallback((captions: VideoCaption[]) => {
    if (!selectedItem || selectedItem.type !== 'video' || !selectedItem.videoConfig) return;
    const updatedItems = mediaItems.map(item =>
      item.id === selectedItemId && item.videoConfig
        ? { ...item, videoConfig: { ...item.videoConfig, captions } }
        : item
    );
    onMediaItemsChange(updatedItems);
  }, [mediaItems, selectedItemId, selectedItem, onMediaItemsChange]);

  const handleDeleteVideo = useCallback(() => {
    if (selectedItem) {
      handleDeleteItem(selectedItem.id);
    }
  }, [selectedItem, handleDeleteItem]);

  // State for the drop overlay on the preview area
  const [isDroppingOnPreview, setIsDroppingOnPreview] = useState(false);
  const dropCounterRef = useRef(0);

  // --- Render ---

  // Empty state: show drag-and-drop zone
  if (mediaItems.length === 0) {
    return (
      <div className="relative">
        <div
          className="border-2 border-dashed border-border rounded-lg p-12 text-center hover:border-border transition-colors bg-muted/50"
          onDragOver={(e) => {
            e.preventDefault();
            e.currentTarget.classList.add('border-primary', 'bg-primary/10');
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            e.currentTarget.classList.remove('border-primary', 'bg-primary/10');
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.currentTarget.classList.remove('border-primary', 'bg-primary/10');
            const files = Array.from(e.dataTransfer.files);
            handleMultipleFiles(files);
          }}
        >
          <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Add Media
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Drag and drop images or videos here, or click to browse.
            <br />
            You can add multiple files at once.
          </p>
          <Button onClick={handleAddMedia} variant="outline">
            <Plus className="w-4 h-4 mr-2" />
            Choose Files
          </Button>
        </div>
      </div>
    );
  }

  // Media loaded: show thumbnail gallery (when 2+) + preview of selected item
  return (
    <div className="space-y-4">
      {/* Thumbnail gallery - only renders when 2+ items */}
      <MediaThumbnailGallery
        mediaItems={mediaItems}
        selectedItemId={selectedItemId}
        onSelectItem={setSelectedItemId}
        onDeleteItem={handleDeleteItem}
        onReorder={handleReorder}
        onAddMore={handleAddMedia}
        selectedPlatforms={selectedPlatformNames}
        disabled={uploading}
      />

      {/* Current media preview - wrapped with capture-phase drop interceptor */}
      {selectedItem && (
        <div
          className="relative"
          onDragEnterCapture={(e) => {
            if (e.dataTransfer.types.includes('Files')) {
              dropCounterRef.current++;
              setIsDroppingOnPreview(true);
            }
          }}
          onDragLeaveCapture={(e) => {
            if (e.dataTransfer.types.includes('Files')) {
              dropCounterRef.current--;
              if (dropCounterRef.current <= 0) {
                dropCounterRef.current = 0;
                setIsDroppingOnPreview(false);
              }
            }
          }}
          onDragOverCapture={(e) => {
            // Capture phase fires BEFORE MediaPreview's handler
            if (e.dataTransfer.types.includes('Files')) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onDropCapture={(e) => {
            if (e.dataTransfer.files.length > 0) {
              e.preventDefault();
              e.stopPropagation();
              dropCounterRef.current = 0;
              setIsDroppingOnPreview(false);
              const files = Array.from(e.dataTransfer.files);
              handleMultipleFiles(files);
            }
          }}
        >
          {/* Drop overlay */}
          {isDroppingOnPreview && (
            <div className="absolute inset-0 z-50 rounded-3xl bg-primary/10 border-2 border-dashed border-primary flex items-center justify-center backdrop-blur-sm pointer-events-none">
              <div className="flex flex-col items-center gap-2 text-primary">
                <Upload className="w-10 h-10" />
                <span className="text-sm font-semibold">Drop to add media</span>
              </div>
            </div>
          )}
          <MediaPreview
            mediaPath={selectedItem.path || ''}
            mediaPreview={selectedItem.preview || ''}
            onMediaPathChange={handleMediaPathChange}
            onMediaPreviewChange={handleMediaPreviewChange}
            onError={onError}
            onWarning={onWarning}
            onVideoStartTimeChange={handleVideoStartTimeChange}
            onVideoEndTimeChange={handleVideoEndTimeChange}
            videoStartTime={selectedItem.videoConfig?.startTime || null}
            videoEndTime={selectedItem.videoConfig?.endTime || null}
            onFileChange={handleFileChange}
            onDeleteVideo={handleDeleteVideo}
            onVideoDurationChange={handleVideoDurationChange}
            needsMobileFormat={needsMobileFormat}
            cropData={selectedItem.videoConfig?.desktopConfig?.cropData || null}
            onOpenCropper={onOpenCropper}
            uploading={uploading}
            generatingCaptions={generatingCaptions}
            onCaptionsChange={handleCaptionsChange}
            initialCaptions={selectedItem.videoConfig?.captions || []}
            crop={selectedItem.videoConfig?.desktopConfig?.crop || null}
            zoom={selectedItem.videoConfig?.desktopConfig?.zoom || 1}
            localFile={selectedItem.localFile}
            onRecommendVideo={onRecommendVideo}
            recommendingVideo={recommendingVideo}
            onCategorySelect={onCategorySelect}
            selectedCategory={selectedCategory}
            unifyFormat={unifyFormat}
            onUnifyFormatChange={onUnifyFormatChange}
            showUnifyOption={showUnifyOption}
            maxDuration={maxDuration}
          />
        </div>
      )}
    </div>
  );
}
